/**
 * Home health demo — a sentence with a clock time inside it.
 *
 * The engine composes plenty of prose that names an hour: *"No reply by 17:00"*,
 * *"overlaps R. Whitfield 11:00–11:45"*, *"by Fri 07:40 · 43h 40m left"*. Those
 * hours have to follow the user's own twelve/twenty-four-hour setting, and that
 * setting is React context — which a pure selector cannot read.
 *
 * So the engine stops phrasing the hour and hands back the **parts** instead: a
 * list of literal strings with a time token wherever a clock time belongs. The
 * view renders it through `useBranchClock`, which knows the preference; anything
 * outside React renders it through `currentBranchClock`; engine specs render it
 * through `plainSentence`, which gives back the 24-hour `HH:mm` the engine used
 * to produce, so a spec still reads as the sentence it is asserting on.
 *
 * A bare `string` is a valid sentence. Most of what the engine composes carries
 * no time at all, and none of it had to change.
 *
 * Two kinds of token, because the demo holds a time of day two ways:
 *
 * - `{ timeIso }` — an instant. Rendered in the **branch** zone, never the
 *   browser's.
 * - `{ hhmm }` — a branch-zone wall clock, the `"HH:mm"` the store, the fixtures
 *   and the plan visits keep. Those strings are *values*: they are parsed,
 *   compared and sorted, so they stay exactly as they are and only their
 *   rendering changes.
 */
import { formatInTimeZone } from "date-fns-tz";

/** An ISO instant. Spelled out rather than imported so the store may name this module's types without a cycle. */
type IsoString = string;

/** A clock time held as an instant. Rendered in the branch zone. */
export interface InstantTime {
  readonly timeIso: IsoString;
}

/** A clock time held as a branch-zone `"HH:mm"` wall clock. */
export interface WallTime {
  readonly hhmm: string;
}

export type TimePart = InstantTime | WallTime;

/** Literal text with clock times in it. A bare string is a sentence with no time in it. */
export type TimeSentence = string | ReadonlyArray<string | TimePart>;

export const atInstant = (timeIso: IsoString): InstantTime => ({ timeIso });

export const atWall = (hhmm: string): WallTime => ({ hhmm });

export function isTimePart(part: string | TimePart): part is TimePart {
  return typeof part !== "string";
}

export function isInstantTime(part: TimePart): part is InstantTime {
  return "timeIso" in part;
}

/**
 * The sentence in 24-hour `HH:mm` — what the engine produced before the sweep.
 * For engine specs, and for anywhere a stable, preference-free string is wanted.
 */
export function plainSentence(sentence: TimeSentence, timeZone: string): string {
  if (typeof sentence === "string") return sentence;
  return sentence.map((part) => (typeof part === "string" ? part : isInstantTime(part) ? formatInTimeZone(new Date(part.timeIso), timeZone, "HH:mm") : part.hhmm)).join("");
}

/**
 * Several sentences as one, with a separator between them — the engine's own
 * `" · "` joins, which have to survive as parts rather than being flattened to
 * a string before the preference has been read. An empty separator simply
 * concatenates, so a sentence can be built out of other sentences.
 */
export function joinSentences(sentences: readonly TimeSentence[], separator: string): TimeSentence {
  const out: Array<string | TimePart> = [];
  sentences.forEach((sentence, i) => {
    if (i > 0 && separator) out.push(separator);
    if (typeof sentence === "string") out.push(sentence);
    else out.push(...sentence);
  });
  return out;
}
